import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { registraDipendente } from "../../../lib/services/authService";

const CreaDipendente = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [adminData, setAdminData] = useState({ id_azienda: null, nome_azienda: '' });
  
  const [formData, setFormData] = useState({
    email: '',
    cf: '',
    nome: '',
    cognome: '',
    telefono: '',
    ruolo: 'User', // Defaultna vrijednost u dropdownu
  });

  const [errors, setErrors] = useState({});
  const [limitError, setLimitError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const getAdminContext = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profilo } = await supabase
          .from('profili')
          .select('id_azienda, nome_azienda')
          .eq('id', user.id)
          .single();
        if (profilo) setAdminData(profilo);
      }
    };
    getAdminContext();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = t('create_employee.errors.email_req');
    if (!formData.cf) newErrors.cf = t('create_employee.errors.cf_req');
    if (!formData.nome) newErrors.nome = t('create_employee.errors.name_req');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLimitError(null);
    if (!validateForm()) return;
    if (!adminData.id_azienda) {
      alert(t('create_employee.errors.no_company'));
      return;
    }

    setIsLoading(true);
    try {
      const result = await registraDipendente({
        ...formData,
        password: formData.cf, // CF se koristi kao lozinka
        id_azienda: adminData.id_azienda,
        nome_azienda: adminData.nome_azienda
      });

      // Provjera limita radnika
    if (!result.success && result.limitReached) {
  setLimitError({
    max: result.max,
    message: t('create_employee.errors.limit_reached', { max: result.max })
  });
  window.scrollTo({ top: 0, behavior: 'smooth' }); // Dodaj ovo!
  setIsLoading(false);
  return;
}

      if (!result.success) throw new Error(result.error);

      setIsSuccess(true);
      // Reset forme na početne vrijednosti
      setFormData({ email: '', cf: '', nome: '', cognome: '', telefono: '', ruolo: 'User' });
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (err) {
      setErrors({ general: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-1 overflow-hidden max-w-2xl mx-auto">
      <div className="p-6 border-b border-border bg-muted/30">
        <div className="flex items-center gap-4 mb-2">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg transition-colors text-sm font-medium"
          >
            <Icon name="ArrowLeft" size={16} />
            {t('create_employee.back')}
          </button>
        </div>
        <h2 className="text-xl font-bold text-foreground">{t('create_employee.title')}</h2>
        <p className="text-sm text-muted-foreground">
          {t('create_employee.subtitle')} <strong>{adminData.nome_azienda}</strong>
        </p>
      </div>
      
      <div className="p-6">
        {isSuccess && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 text-green-600 rounded-lg flex items-center gap-3 animate-in fade-in zoom-in">
            <Icon name="CheckCircle" size={20} />
            <span>{t('create_employee.success_msg')}</span>
          </div>
        )}

        {/* PRIKAZ GREŠKE ZA LIMIT */}
        {limitError && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl animate-in slide-in-from-top-2">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" className="text-red-600 mt-1" size={20} />
              <div className="flex-1">
                <h4 className="text-sm font-bold text-red-600">{t('create_employee.errors.limit_title')}</h4>
                <p className="text-xs text-red-600/80 font-medium mt-0.5">{limitError.message}</p>
                <button 
                  onClick={() => navigate('/stats')}
                  className="mt-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 transition-colors"
                >
                  {t('create_employee.errors.upgrade_now')} <Icon name="ArrowRight" size={12} />
                </button>
              </div>
            </div>
          </div>
        )}

        {errors.general && !limitError && (
          <p className="text-red-500 text-sm mb-4 text-center p-2 bg-red-50 rounded">{errors.general}</p>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-primary">
              {t('create_employee.section_title')}
            </h3>
            
            {/* NOVI DROPDOWN ZA ULOGU */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-foreground">
                {t('create_employee.labels.role')}
              </label>
              <select 
                name="ruolo"
                value={formData.ruolo}
                onChange={handleChange}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border-border"
              >
                <option value="User">{t('create_employee.roles.user')}</option>
                <option value="Admin">{t('create_employee.roles.admin')}</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input name="nome" label={t('create_employee.labels.name')} value={formData.nome} onChange={handleChange} error={errors.nome} required />
              <Input name="cognome" label={t('create_employee.labels.surname')} value={formData.cognome} onChange={handleChange} />
            </div>

            <Input 
              name="cf" 
              label={t('create_employee.labels.cf')} 
              value={formData.cf} 
              onChange={handleChange} 
              error={errors.cf} 
              required 
              placeholder={t('create_employee.placeholders.cf')} 
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input type="email" name="email" label={t('create_employee.labels.email')} value={formData.email} onChange={handleChange} error={errors.email} required />
              <Input name="telefono" label={t('create_employee.labels.phone')} value={formData.telefono} onChange={handleChange} placeholder="es. +39..." />
            </div>
          </div>

          <div className="pt-6">
            <Button type="submit" fullWidth loading={isLoading} size="lg">
              {t('create_employee.submit_btn')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreaDipendente;